// OWNER: platform module (offline area). React glue for the outbox: connectivity, replay triggers,
// query invalidation and clock-drift correction. Used once by <OfflineBanner/> (mounted in the AppShell).
import { useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { OfflineSyncStatusResponse } from '@seventide/types';
import { api, wsPath } from '../../../lib/api';
import { useStore } from '../../../lib/store';
import { readStorage, writeStorage } from '../../../lib/storage';
import { offlineQueue, type Outbox } from './outbox';
import type { OutboxItem, OutboxState } from './types';

let clockSyncedForWs: string | null = null;
const CLOCK_CHECKED_KEY = 'st.clockCheckedAt';
const CLOCK_RECHECK_MS = 12 * 60 * 60_000;

/** Checks the server clock at most every 12 hours (GET /platform/sync/status). */
async function syncClock(outbox: Outbox, wsId: string) {
  if (clockSyncedForWs === wsId) return;
  clockSyncedForWs = wsId;
  const last = Number(readStorage(CLOCK_CHECKED_KEY));
  if (Number.isFinite(last) && Date.now() - last < CLOCK_RECHECK_MS) return;
  try {
    const started = Date.now();
    const res = await api.get<OfflineSyncStatusResponse>(wsPath(wsId, 'platform', 'sync', 'status'), {
      redirectOn401: false,
    });
    outbox.setServerTime(res.serverTime, started, Date.now());
    writeStorage(CLOCK_CHECKED_KEY, String(Date.now()));
  } catch {
    clockSyncedForWs = null; // try again next time
  }
}

/**
 * Wires the outbox to the page: sets the signed-in user, replays on start / `online` / page visible,
 * tracks connectivity, refreshes ['w', wsId, module] queries after items are sent.
 */
export function useOfflineSync(opts: {
  userId: string | null | undefined;
  wsId?: string | null;
  outbox?: Outbox;
  /** Correct device clock drift via the server time (default: on, except in unit tests). */
  clockSync?: boolean;
}) {
  const outbox = opts.outbox ?? offlineQueue;
  const queryClient = useQueryClient();
  const { userId, wsId } = opts;
  const clockSync = opts.clockSync ?? import.meta.env.MODE !== 'test';

  useEffect(() => {
    if (userId === undefined) return; // still loading
    outbox.setUser(userId);
    if (userId) void outbox.replay();
  }, [outbox, userId]);

  useEffect(() => {
    const onOnline = () => {
      outbox.setOnline(true);
      void outbox.replay();
    };
    const onOffline = () => outbox.setOnline(false);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void outbox.replay();
    };
    outbox.setOnline(typeof navigator === 'undefined' ? true : navigator.onLine !== false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [outbox]);

  useEffect(
    () =>
      outbox.onSynced(({ workspaceId, modules }) => {
        for (const m of modules) void queryClient.invalidateQueries({ queryKey: ['w', workspaceId, m] });
      }),
    [outbox, queryClient],
  );

  const online = useStore(outbox.state).online;
  useEffect(() => {
    if (clockSync && wsId && userId && online) void syncClock(outbox, wsId);
  }, [outbox, wsId, userId, online, clockSync]);
}

/** Outbox state for rendering (items include bodies — never render their content in shared places). */
export function useOutboxState(outbox: Outbox = offlineQueue): OutboxState {
  return useStore(outbox.state);
}

/**
 * Items waiting to sync for a workspace (optionally one module / entity type), e.g. to show
 * "Waiting to sync" rows in a list:  const pending = useOutboxItems({ workspaceId: wsId, entityType: 'gift' })
 * Each item's `body` is the request body (with `id` = the record id for creates).
 */
export function useOutboxItems(
  filter: { workspaceId: string; module?: string; entityType?: string; includeProblems?: boolean },
  outbox: Outbox = offlineQueue,
): OutboxItem[] {
  const { items } = useStore(outbox.state);
  const { workspaceId, module, entityType, includeProblems } = filter;
  return useMemo(
    () =>
      items.filter(
        (i) =>
          i.workspaceId === workspaceId &&
          (!module || i.module === module) &&
          (!entityType || i.entityType === entityType) &&
          (includeProblems || i.status === 'pending' || i.status === 'blocked'),
      ),
    [items, workspaceId, module, entityType, includeProblems],
  );
}
